import React, { useMemo, useState } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import { colors } from "../utils/datosTheme";
import { formatMoney, formatMonthLabel, formatNumber } from "../utils/datosFormatters";

const categoryColors = [colors.blue, colors.green, colors.amber, colors.coral, colors.teal, "#7d8495"];

function formatCompactMoney(value, currency) {
  const number = Number(value || 0);
  if (!number) return "0";
  const suffixes = [
    { value: 1000000000, label: "MM" },
    { value: 1000000, label: "M" },
    { value: 1000, label: "K" },
  ];
  const suffix = suffixes.find((item) => Math.abs(number) >= item.value);
  if (!suffix) return formatMoney(number, currency);
  const compact = number / suffix.value;
  return `${currency === "ARS" ? "$ " : ""}${compact.toLocaleString("es-AR", { maximumFractionDigits: 1 })}${suffix.label}`;
}

function salesMoneyTooltip(value, name, currency) {
  if (name === "Monto vendido") return formatMoney(value, currency);
  return formatNumber(value);
}

function MetricToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next) => next && onChange(next)}
      sx={{
        "& .MuiToggleButton-root": {
          minWidth: 132,
          height: 38,
          px: 1.6,
          py: 0,
          color: colors.blue,
          borderColor: colors.line,
          backgroundColor: "rgba(255,255,255,0.88)",
          fontSize: 13,
          fontWeight: 850,
          lineHeight: 1.2,
          textTransform: "none",
          "&.Mui-selected": {
            bgcolor: colors.blue,
            color: "#fff",
            borderColor: colors.blue,
            boxShadow: "none",
            "&:hover": { bgcolor: "#004be0", borderColor: "#004be0" },
          },
        },
      }}
    >
      <ToggleButton value="ventas" aria-label="cantidad-ventas">Cantidad de ventas</ToggleButton>
      <ToggleButton value="monto" aria-label="monto-vendido">Monto vendido</ToggleButton>
    </ToggleButtonGroup>
  );
}

function MetricChartTitle({ metric, onMetricChange, suffix }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <MetricToggle value={metric} onChange={onMetricChange} />
      <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15 }}>
        {suffix}
      </Typography>
    </Box>
  );
}

function ChartTitle({ title, description }) {
  return (
    <Box>
      <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15 }}>
        {title}
      </Typography>
      <Typography sx={{ color: colors.muted, fontWeight: 650, fontSize: 11.5, mt: 0.35, lineHeight: 1.25 }}>
        {description}
      </Typography>
    </Box>
  );
}

export default function VentasDashboard({ data }) {
  const [monthlyMetric, setMonthlyMetric] = useState("ventas");
  const kpis = data?.kpis || {};
  const currency = kpis.moneda_principal;
  const monthly = useMemo(
    () => (data?.mensual || []).map((item) => ({ ...item, label: formatMonthLabel(item.mes) })),
    [data]
  );
  const formasPago = data?.formas_pago || [];

  const hasMonthlyData = monthly.some((item) => Number(item.ventas || 0) > 0 || Number(item.monto_vendido || 0) > 0);
  const hasPayments = formasPago.some((item) => Number(item.total || 0) > 0);

  const kpiDescriptions = {
    ventas_cerradas: "Ventas registradas en el período",
    monto_vendido: `${formatNumber(kpis.ventas_cerradas)} vehículos vendidos`,
    ticket_promedio: "Monto vendido / ventas cerradas",
    tiempo_promedio_cierre_dias: "Promedio de fecha venta - fecha inicio",
    ventas_contado: "Operaciones detectadas como contado",
    ventas_financiadas: "Operaciones con financiación o monto financiado",
    ventas_con_usado: "Ventas con usado en parte de pago",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "ventas_cerradas",
          "monto_vendido",
          "ticket_promedio",
          "tiempo_promedio_cierre_dias",
          "ventas_contado",
          "ventas_financiadas",
          "ventas_con_usado",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={kpis[id]}
            currency={currency}
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel
          title={<MetricChartTitle metric={monthlyMetric} onMetricChange={setMonthlyMetric} suffix="por mes" />}
          empty={!hasMonthlyData}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: colors.muted, fontSize: 12 }}
                allowDecimals={monthlyMetric === "ventas" ? false : undefined}
                tickFormatter={monthlyMetric === "monto" ? (value) => formatCompactMoney(value, currency) : undefined}
              />
              <RechartsTooltip formatter={(value, name) => salesMoneyTooltip(value, name, currency)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              {monthlyMetric === "ventas" ? (
                <Bar dataKey="ventas" name="Ventas" fill={colors.blue} radius={[5, 5, 0, 0]} />
              ) : (
                <Bar dataKey="monto_vendido" name="Monto vendido" fill={colors.green} radius={[5, 5, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel
          title={
            <ChartTitle
              title="Ventas por forma de pago"
              description="Distribución de las ventas cerradas según la forma de pago principal."
            />
          }
          empty={!hasPayments}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={formasPago}
                dataKey="total"
                nameKey="label"
                innerRadius="48%"
                outerRadius="76%"
                paddingAngle={2}
                label={({ label, total }) => `${label}: ${total}`}
              >
                {formasPago.map((entry, index) => (
                  <Cell key={entry.label} fill={categoryColors[index % categoryColors.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>
    </>
  );
}
